<script setup lang="ts">
import { QDialog } from 'quasar'
import { Color } from '@previo/codebooks'
import PBtn from '../btn/PBtn.vue'
import { PBtnType } from '../btn/types'
import PCard from '../card/PCard.vue'
import { PCardRound, PCardSize } from '../card/types'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import { usePrevioDS } from '../../composables/helpers'
import { PDialogPosition } from '../dialog/types'
import { PGlobalMenuItem, PGlobalMenuWithSubMenu } from './types'
import PSideMenuExpansion from './PSideMenuExpansion.vue'

type Props = {
  items: (PGlobalMenuWithSubMenu | PGlobalMenuItem)[]
}

type Emits = {
  (e: 'close'): void
}

const model = defineModel<boolean>()

withDefaults(defineProps<Props>(), {})

const emit = defineEmits<Emits>()

const ds = usePrevioDS()

const close = (): void => {
  emit('close')
}
</script>

<template>
  <q-dialog
    v-model="model"
    :breakpoint="ds.screen().breakpoints.laptopWidthMax"
    :position="PDialogPosition.END"
    full-height
    maximized
    overlay
  >
    <p-card
      class="p-display-flex p-flex-direction-column ds-mobile-menu"
      :color="Color.WHITE"
      :pa-mobile="PCardSize.SM"
      :round-top-start="PCardRound.XL"
      :round-bottom-start="PCardRound.XL"
    >
      <div class="p-flex">
        <img src="../../public/logo/previo-color.svg" height="35" alt="Previo logo" />
        <p-space :mobile="PSpaceSize.GROW" />
        <p-btn :type="PBtnType.OUTLINE" icon-end="xmark-large" cy="close" @click="close" />
      </div>

      <template v-if="$slots['side-menu-actions']">
        <p-space :mobile="PSpaceSize.LG" vertical />

        <div class="p-flex p-no-wrap">
          <slot name="side-menu-actions" />
        </div>
      </template>

      <p-space :mobile="$slots['side-menu-actions'] ? PSpaceSize.SM : PSpaceSize.XXL" vertical />

      <p-card
        class="p-display-flex p-flex-direction-column ds-mobile-menu-list"
        :round="PCardRound.MD"
        :pa-mobile="PCardSize.SM"
        show-border
        :color="Color.GREY_50"
      >
        <div class="p-scroll-y">
          <p-side-menu-expansion :items="items" :menu-index="1" @close-menu="model = false">
            <template
              v-if="$slots['side-menu-item']"
              #side-menu-item="{ item }: { item: PGlobalMenuItem }"
            >
              <slot name="side-menu-item" :item="item" />
            </template>
          </p-side-menu-expansion>
        </div>
      </p-card>

      <template v-if="$slots['side-menu-footer']">
        <p-space :mobile="PSpaceSize.SM" vertical />

        <slot name="side-menu-footer" />
      </template>
    </p-card>
  </q-dialog>
</template>

<style lang="scss" scoped>
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/variables/ds.spacing';

.ds-mobile-menu {
  @include safe-area-padding(top, $space-sm-mobile);
  @include safe-area-padding(right, $space-sm-mobile);
  @include safe-area-padding(bottom, $space-sm-mobile);
  width: 316px;
}

.ds-mobile-menu-list {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
